import eq from "lodash-es/eq"

import { buildCssClassName, getLinkDecorationTwClasses, getTransitionTwClasses } from "@/3514/utils"

import {
    ECoachDashboardActiveTab as EActiveTab,
    ECoachDashboardTableDataType as ETableType
} from "./coach.dashboard.ui.types"

export default {
    page: {
        container: (): string =>
            buildCssClassName(
                "flex",
                "flex-col",
                "flex-1",
                "gap-y-[20px]",
                "px-[0px]",
                "md:px-[30px]",
                "xl:px-[60px]",
                "pb-[100px]",
                "xxl:max-w-[1800px]",
                "xxl:w-full",
                "xxl:mx-auto"
            )
    },
    title: { container: (): string => "px-[15px] md:px-[0px] xl:pb-[5px] xl:px-[40px] xl:pt-[10px]" },
    notifications: {
        container: (): string => "flex flex-col gap-y-[10px] mb-[10px] md:mb-[20px] relative md:px-[10px] xl:px-[20px]",
        fallback: (): string => "ml-[10px] md:ml-0 md:mr-auto"
    },
    attendancesEntity: {
        container: (): string => "flex flex-col gap-y-[10px]",
        sentinel: (): string => "absolute top-0",
        errorBoxContainer: (): string =>
            "!rounded-[0px] md:!rounded-[10px] !pt-[10px] !pb-[15px] !px-[15px] md:!p-[20px]",
        list: (): string => "flex flex-col",
        listWrapper: (): string => "flex flex-col gap-y-[10px]",
        item: ({ isOpenSidebar }: { isOpenSidebar: boolean }): string =>
            buildCssClassName(
                "[display:grid]",
                "grid-cols-[auto_1fr_auto]",
                { "md:grid-cols-[105px_1fr_1fr_minmax(max-content,110px)]": !isOpenSidebar },
                { "md:grid-cols-[105px_1fr_minmax(max-content,110px)]": isOpenSidebar },
                {
                    "lg:grid-cols-[120px_minmax(100px,auto)_minmax(100px,auto)_minmax(max-content,150px)]":
                        !isOpenSidebar
                },
                {
                    "lg:grid-cols-[120px_minmax(100px,auto)_minmax(max-content,150px)]": isOpenSidebar
                },
                "xl:grid-cols-[140px_minmax(100px,auto)_minmax(100px,auto)_minmax(max-content,150px)]",
                "items-center",
                "gap-x-[20px]",
                "pt-[20px]",
                "px-[20px]",
                "pb-[25px]",
                "rounded-[10px]",
                "bg-white",
                getTransitionTwClasses("all", 200)
            ),
        itemCol: (): string => "flex flex-col items-center",
        itemTruncatedText: (): string => "truncate max-w-[90px] md:max-w-[180px] lg:max-w-[300px]",
        listBtn: (): string => "my-[5px]"
    },
    dataPanelEntity: {
        container: {
            section: (): string =>
                buildCssClassName("flex", "flex-col", "px-[15px]", "md:px-[0px]", "lg:px-[30px]", "xl:px-[20px]"),
            header: (): string =>
                buildCssClassName(
                    "pl-[10px]",
                    "pt-[10px]",
                    "md:px-[10px]",
                    "md:pt-[20px]",
                    "md:pb-[10px]",
                    "xl:px-[20px]",
                    "xl:pt-[20px]",
                    "[display:grid]",
                    "grid-cols-1",
                    "gap-y-[10px]",
                    "md:gap-y-[30px]"
                ),
            headerFirstRow: (): string =>
                buildCssClassName(
                    "[display:grid]",
                    "grid-cols-1",
                    "md:grid-cols-[250px_300px]",
                    "xl:grid-cols-[300px_300px]",
                    "md:justify-between",
                    "gap-y-[20px]"
                ),
            headerSecondRow: (): string =>
                buildCssClassName(
                    "hidden",
                    "md:[display:grid]",
                    "md:grid-cols-[auto_150px]",
                    "md:justify-between",
                    "md:items-center",
                    "md:gap-x-[10px]"
                ),
            headerThirdRow: (): string =>
                buildCssClassName("flex", "flex-col", "md:flex-row", "md:justify-between", "md:items-center"),
            loader: (): string => "mt-[40px]"
        },
        input: (): string =>
            buildCssClassName(
                "w-full",
                "h-[42px]",
                "py-[10px]",
                "px-[15px]",
                "placeholder:italic",
                "placeholder:font-[16px]",
                "placeholder:text-gray-100",
                "text-gray-100",
                "bg-gray-500",
                "rounded-[10px]",
                "border-[1px]",
                "border-transparent",
                "hover:border-gray-300",
                "focus:border-gray-300",
                getTransitionTwClasses("all", 100)
            ),
        tabs: {
            container: (): string => "flex gap-x-[25px] py-[5px] md:px-[10px] justify-center md:justify-start",
            tab: ({ isActive }: { isActive: boolean }): string =>
                buildCssClassName(
                    "cursor-pointer",
                    isActive && getLinkDecorationTwClasses({ color: "accent", offset: 3 })
                )
        },
        table: {
            container: (): string => "flex flex-col gap-y-[70px] md:gap-y-[40px] xl:gap-y-[60px]",
            table: (): string => "flex flex-col w-full",
            heading: {
                container: ({ activeTab }: { activeTab: EActiveTab }): string =>
                    buildCssClassName(
                        "w-full",
                        "[display:grid]",
                        "md:gap-x-[20px]",
                        "md:border-b-[1px]",
                        "md:border-blue-400",
                        "justify-between",
                        eq(activeTab, EActiveTab.Sessions) &&
                            "grid-cols-[minmax(auto,auto)_120px] md:grid-cols-[140px_1fr_1fr_1fr]",
                        eq(activeTab, EActiveTab.Participants) && [
                            "grid-cols-[85px]",
                            "md:grid-cols-[25px_140px_1fr_1fr_1fr]",
                            "lg:grid-cols-[45px_140px_1fr_1fr_1fr]"
                        ]
                    ),
                header: ({ isSortable }: { isSortable: boolean }): string =>
                    buildCssClassName(
                        "flex",
                        "gap-x-[5px]",
                        "items-center",
                        "justify-center",
                        "pb-[8px]",
                        "md:pb-[10px]",
                        "px-[5px]",
                        isSortable && ["border-b-[1px]", "border-accent", "cursor-pointer", "group"]
                    ),
                text: ({ isSortable }: { isSortable: boolean }): string =>
                    buildCssClassName(
                        "select-none",
                        isSortable && [
                            "group-hover:text-accent",
                            "group-focus:text-accent",
                            getTransitionTwClasses("all", 100)
                        ]
                    )
            },
            cel: {
                col: (
                    { isContentTopAligned }: { isContentTopAligned: boolean } = { isContentTopAligned: true }
                ): string =>
                    buildCssClassName(
                        "flex flex-col justify-start items-center",
                        isContentTopAligned ? "justify-start" : "justify-center"
                    ),
                row: ({ isSessionLive }: { isSessionLive?: boolean }): string =>
                    buildCssClassName(
                        "md:[display:grid] lg:flex lg:flex-row md:items-center lg:gap-x-[10px] xl:[display:grid]",
                        isSessionLive
                            ? [
                                  "justify-end",
                                  "xxl:justify-center",
                                  "md:grid-cols-[max-content_100px]",
                                  "xl:grid-cols-[max-content_115px]"
                              ]
                            : "justify-center"
                    ),
                truncatedText: (): string => "max-w-[100%] truncate text-ellipsis"
            },
            row: <TType>({ type }: { type: TType }): string =>
                buildCssClassName(
                    "w-full",
                    "[display:grid]",
                    "gap-x-[10px]",
                    "xl:gap-x-[20px]",
                    "px-[10px]",
                    "md:px-[20px]",
                    "rounded-[10px]",
                    "bg-white",
                    "border-[1px]",
                    "border-transparent",
                    "hover:bg-blue-600",
                    "focus:bg-blue-600",
                    "focus:border-blue-400",
                    "cursor-pointer",
                    getTransitionTwClasses("colors", 100),
                    eq(type, ETableType.Sessions) && [
                        "py-[15px]",
                        "md:pt-[20px]",
                        "md:pb-[25px]",
                        "grid-cols-[minmax(0px,105px)_minmax(155px,1fr)]",
                        "md:grid-cols-[105px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]",
                        "lg:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
                    ],
                    eq(type, ETableType.Participants) && [
                        "pt-[20px]",
                        "pb-[25px]",
                        "grid-cols-[33%_33%_33%]",
                        "md:grid-cols-[25px_140px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]",
                        "xl:grid-cols-[45px_140px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
                    ]
                ),
            pagination: {
                container: (): string => "flex items-center self-center gap-x-[15px]",
                list: (): string => "flex items-center gap-x-[7px]",
                filler: (): string => "w-[27px] h-[27px] [cursor:default] flex items-center justify-center",
                fillerValue: (): string => "select-none",
                bullet: ({ isCurrent }: { isCurrent: boolean }): string =>
                    buildCssClassName(
                        "w-[27px] h-[27px] p-[2px] flex items-center justify-center rounded-full",
                        getTransitionTwClasses("colors", 100),
                        isCurrent ? "bg-accent" : "bg-gray-500 hover:bg-blue-400 focus:bg-blue-400"
                    ),
                bulletNumber: (): string => buildCssClassName(getTransitionTwClasses("all", 100), "select-none"),
                arrow: ({ isDisabled }: { isDisabled: boolean }): string =>
                    buildCssClassName(
                        "w-[20px] h-[20px] flex items-center justify-center",
                        isDisabled ? "![cursor:default]" : "[cursor:pointer] "
                    )
            }
        }
    }
} as const
